import {
  chaseProgress,
  debtorCount,
  recentTrend,
  chaseToday,
  fmt,
  fmtDate,
  fmtK,
  initialOf,
  type ActivityEntry,
} from '@k2pos/core';
import { Link, useNavigate } from 'react-router-dom';
import { HazardStripes } from '@/components/brand/HazardStripes';
import { K2Logo } from '@/components/brand/K2Logo';
import { ProgressRing } from '@/components/brand/ProgressRing';
import { SparkBar } from '@/components/brand/SparkBar';
import { AppBar } from '@/components/ui/AppBar';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';
import { Mono, Text } from '@/components/ui/Text';
import { IconPhone } from '@/components/icons';
import { totalDebt, usePos } from '@/store/pos';
import { colors } from '@/theme/tokens';
import styles from './Home.module.css';

/** Daily pulse: what is owed, who to chase today, and one tap to sell or collect. */
export function Home() {
  const navigate = useNavigate();
  const customers = usePos((s) => s.customers);
  const activity = usePos((s) => s.activity);
  const salesToday = usePos((s) => s.salesToday);
  const receivedToday = usePos((s) => s.receivedToday);
  const shop = usePos((s) => s.shop);
  const collectedToday = usePos((s) => s.collectedToday);
  const journal = usePos((s) => s.journal);
  const currentDay = usePos((s) => s.currentDay);

  const outstanding = totalDebt(customers);
  // The shop's own threshold, not the default 0: without it the card chased the
  // oldest balance on the books whatever ຕັ້ງເຕືອນທວງໜີ້ was set to, so the
  // setting existed and did nothing.
  const chase = chaseToday(customers, shop.reminderDays);
  const newCredit = salesToday - receivedToday;

  return (
    <>
      <AppBar
        title={shop.name}
        subtitle={
          <Mono size={10} color={colors.textFaint}>
            ສະບາຍດີ · {fmtDate(new Date())}
          </Mono>
        }
        leading={<K2Logo size={34} />}
        right={
          <div className={styles.avatar}>
            <Text weight={800} size={14} color={colors.sky}>
              {initialOf(shop)}
            </Text>
          </div>
        }
      />

      <div className={`${styles.body} vscroll`}>
        <DebtHero
          outstanding={outstanding}
          debtors={debtorCount(customers)}
          progress={chaseProgress(collectedToday, outstanding)}
          trend={recentTrend(journal, currentDay)}
        />

        {chase ? (
          <Card elevated className={styles.chaseCard}>
            <div className={styles.chaseHead}>
              <span className={styles.dot} />
              <Text weight={800} size={11} color={colors.skyDeep}>
                ຄວນທວງມື້ນີ້
              </Text>
            </div>

            <div className={styles.chaseRow}>
              <div className="grow col">
                <Text weight={700} size={14} lines={1}>
                  {chase.name}
                </Text>
                <Text size={11} color={colors.textMuted} lines={1}>
                  ຕິດ {fmt(chase.balance)} ₭ ·{' '}
                  <Text size={11} weight={700} color={colors.skyDeep}>
                    {chase.days} ມື້
                  </Text>
                </Text>
              </div>

              {/* A tel: link, not a button. In the shell the WebView hands it to
                  the dialler; in a browser it does the same thing natively. */}
              <a
                href={`tel:${chase.phone.replace(/\s/g, '')}`}
                aria-label={`ໂທຫາ ${chase.name}`}
                className={styles.callBtn}
              >
                <IconPhone size={17} />
              </a>

              <Button
                label="ທວງ"
                variant="solid"
                size={13}
                className={styles.chaseCta}
                onPress={() => navigate(`/customer/${chase.id}`)}
              />
            </div>
          </Card>
        ) : null}

        <div className={styles.tiles}>
          <StatTile label="ຍອດຂາຍ" value={fmtK(salesToday)} sub="ລວມຕິດ" />
          <StatTile label="ເງິນເຂົ້າ" value={fmtK(receivedToday)} sub="ຈ່າຍເລີຍ" />
          <StatTile label="ຕິດໜີ້ໃໝ່" value={fmtK(newCredit)} sub="= ຂາຍ−ເຂົ້າ" accent />
        </div>

        <div className={styles.actions}>
          <Button label="＋ ຂາຍ" className="grow" onPress={() => navigate('/sell')} />
          <Button
            label="ທວງໜີ້"
            variant="outline"
            className="grow"
            onPress={() => navigate('/debt')}
          />
        </div>

        <Text weight={800} size={12} color={colors.textMuted} className={styles.sectionLabel}>
          ລ່າສຸດ
        </Text>
        {activity.slice(0, 6).map((entry) => (
          <ActivityRow key={entry.id} entry={entry} />
        ))}
      </div>
    </>
  );
}

/** The emotional core of the product, given the largest surface on the screen. */
function DebtHero({
  outstanding,
  debtors,
  progress,
  trend,
}: {
  outstanding: number;
  debtors: number;
  /** Undefined when there is nothing to measure — see below. */
  progress: number | undefined;
  trend: number[];
}) {
  return (
    <div className={styles.hero}>
      <HazardStripes className={styles.heroStripes} opacity={0.35} period={18} />

      <div className={styles.heroTop}>
        <div className="grow col">
          <Text size={12} color={colors.onDarkBright}>
            ໜີ້ຄ້າງທັງໝົດ
          </Text>
          <Mono weight={700} size={31} color="#FFFFFF" className={`${styles.heroAmount} selectable`}>
            {fmt(outstanding)}
          </Mono>
          <Text size={11} color={colors.onDarkSoft}>
            ₭ · {debtors} ຄົນຕິດຢູ່
          </Text>
        </div>
        {/* Absent, not 0%. A shop that is owed nothing has no chase to be
            60% — or 0% — through, and inventing a figure on the screen the
            owner trusts most is worse than leaving the space empty. */}
        {progress === undefined ? null : <ProgressRing value={progress} />}
      </div>

      {trend.length > 0 ? <SparkBar values={trend} /> : null}
    </div>
  );
}

function StatTile({
  label,
  value,
  sub,
  accent = false,
}: {
  label: string;
  value: string;
  sub: string;
  accent?: boolean;
}) {
  return (
    <Card className={styles.tile}>
      <Text size={10} color={colors.textMuted} lines={1}>
        {label}
      </Text>
      <Mono
        weight={700}
        size={13}
        color={accent ? colors.skyDeep : colors.text}
        className={`${styles.tileValue} selectable`}
      >
        {value}
      </Mono>
      <Text size={8} color={colors.textFaint} lines={1}>
        {sub}
      </Text>
    </Card>
  );
}

function ActivityRow({ entry }: { entry: ActivityEntry }) {
  const credit = entry.kind === 'credit';
  // Activity ids are the source event's id with an 'a' prefix, so a row can link
  // straight to the bill it came from. Seeded mock rows have no such event, and
  // the order screen shows a "not found" rather than pretending otherwise.
  const orderId = entry.id.startsWith('as') ? entry.id.slice(1) : null;

  const body = (
    <Card
      accent={credit ? colors.skyDeep : colors.navy}
      accentWidth={3}
      className={styles.activityRow}
    >
      <div className="grow col">
        <Text weight={700} size={12.5} lines={1}>
          {entry.customerName}
        </Text>
        <Text size={10.5} color={colors.textMuted} lines={1}>
          {entry.detail}
        </Text>
      </div>
      <Mono weight={700} size={13} color={credit ? colors.skyDeep : colors.text}>
        {credit ? '+' : ''}
        {fmtK(entry.amount)}
      </Mono>
    </Card>
  );

  if (!orderId) return body;
  return (
    <Link to={`/orders/${orderId}`} className={styles.activityLink}>
      {body}
    </Link>
  );
}
